// Single Lane Gantt Chart data structure
// 單一車道甘特圖的資料結構，符合規格要求

export interface OperationData {
  id: string;
  name: string;
  assignee: string;
  start: string;
  end: string;
  progress: number;
}

export interface DepartmentData {
  id: string;
  name: string;
  group: number; // 並行組編號 (G1, G2, ...)
  start: string;
  end: string;
  progress: number;
  operations: OperationData[];
}

export interface StageData {
  id: string;
  name: string;
  group: number; // 並行組編號
  start: string;
  end: string;
  progress: number;
  departments: DepartmentData[];
}

export interface OrderData {
  orderId: string;
  customer: string;
  product: string;
  quantity: number;
  priority: "high" | "medium" | "low";
  progress: number;
  start: string; // 由子層最小開始時間計算
  end: string;   // 由子層最大結束時間計算
  dueDate: string; // 預計交貨日期
  stages: StageData[];
}

// 計算匯總時間的工具函數
export const calculateOrderSummary = (stages: StageData[]): { start: string; end: string } => {
  if (stages.length === 0) return { start: '', end: '' };
  
  const startDates = stages.map(s => new Date(s.start)).filter(d => !isNaN(d.getTime()));
  const endDates = stages.map(s => new Date(s.end)).filter(d => !isNaN(d.getTime()));
  
  if (startDates.length === 0 || endDates.length === 0) return { start: '', end: '' };
  
  const minStart = new Date(Math.min(...startDates.map(d => d.getTime())));
  const maxEnd = new Date(Math.max(...endDates.map(d => d.getTime())));
  
  return {
    start: minStart.toISOString().split('T')[0] + 'T08:00:00',
    end: maxEnd.toISOString().split('T')[0] + 'T18:00:00'
  };
};

export const calculateStageSummary = (departments: DepartmentData[]): { start: string; end: string } => {
  if (departments.length === 0) return { start: '', end: '' };
  
  const startDates = departments.map(d => new Date(d.start)).filter(date => !isNaN(date.getTime()));
  const endDates = departments.map(d => new Date(d.end)).filter(date => !isNaN(date.getTime()));
  
  if (startDates.length === 0 || endDates.length === 0) return { start: '', end: '' };
  
  const minStart = new Date(Math.min(...startDates.map(d => d.getTime())));
  const maxEnd = new Date(Math.max(...endDates.map(d => d.getTime())));
  
  return {
    start: minStart.toISOString().split('T')[0] + 'T08:00:00',
    end: maxEnd.toISOString().split('T')[0] + 'T18:00:00'
  };
};

export const calculateDepartmentSummary = (operations: OperationData[]): { start: string; end: string } => {
  if (operations.length === 0) return { start: '', end: '' };
  
  const startDates = operations.map(o => new Date(o.start)).filter(date => !isNaN(date.getTime()));
  const endDates = operations.map(o => new Date(o.end)).filter(date => !isNaN(date.getTime()));
  
  if (startDates.length === 0 || endDates.length === 0) return { start: '', end: '' };
  
  const minStart = new Date(Math.min(...startDates.map(d => d.getTime())));
  const maxEnd = new Date(Math.max(...endDates.map(d => d.getTime())));
  
  return {
    start: minStart.toISOString().split('T')[0] + 'T08:00:00',
    end: maxEnd.toISOString().split('T')[0] + 'T18:00:00'
  };
};


// 並行組顏色映射
export const getGroupColor = (groupNumber: number): string => {
  const colors = [
    'border-blue-500 bg-blue-50',
    'border-green-500 bg-green-50', 
    'border-orange-500 bg-orange-50',
    'border-purple-500 bg-purple-50',
    'border-red-500 bg-red-50',
    'border-teal-500 bg-teal-50'
  ];
  return colors[(groupNumber - 1) % colors.length] || colors[0];
};

// 並行組徽章顏色
export const getGroupBadgeColor = (groupNumber: number): string => {
  const colors = [
    'bg-blue-500 text-white',
    'bg-green-500 text-white',
    'bg-orange-500 text-white',
    'bg-purple-500 text-white',
    'bg-red-500 text-white',
    'bg-teal-500 text-white'
  ];
  return colors[(groupNumber - 1) % colors.length] || colors[0];
};